import React, { Component } from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import Axios from 'axios';
import fLoading from '../assets/img/preloader-black.gif';
import imgfSelectLeft from '../pages/default-v2/img/select-left.svg';
import url from '../config/Url';

/**
 * Wrapper de debug - só imprime logs se localStorage.capsulaDebug === "true"
 */
const debug = (...args) => {
  if (localStorage.getItem("capsulaDebug") === "true") {
    console.log(...args);
  }
};

export default class TreeViewLayer extends Component {
  constructor(oProps) {
    super(oProps);
    this.state = {
      treeData: null,
      expandedNodes: {},
      loadingNodes: {},
      selectedFolderId: oProps.nFolderId || null,
      navigationStack: [],
      currentFolderInfo: null,
      loadingRoot: true,
    };
    
    this.isLoadingInitial = false;
  }

  componentDidMount() {
    if (!this.isLoadingInitial) {
      this.isLoadingInitial = true;
      const { nFolderId } = this.props;
      
      if (nFolderId) {
        this.loadFromSpecificFolder(nFolderId);
      } else {
        this.loadFolderTree();
      }
    }
  }

  /**
   * 🔹 Busca o nome da pasta consultando seu parent
   */
  fetchFolderName = async (folderId, parentId) => {
    try {
      debug("Buscando nome da pasta", folderId, "no parent", parentId);
      
      // Se parentId for null ou undefined, busca na raiz
      const params = parentId ? { nParentId: parentId } : {};
      
      const res = await Axios.get(`${url.cBase}api/folder/tree`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('cAccessToken')}`
        }
      });
      
      debug("Resposta do parent:", res.data);
      
      // Procura a pasta nos children
      if (res.data.children && Array.isArray(res.data.children)) {
        const folder = res.data.children.find(child => 
          parseInt(child.fldr_nid) === parseInt(folderId)
        );
        
        if (folder) {
          debug("Nome encontrado:", folder.fldr_cname);
          return folder.fldr_cname;
        }
      }
      
      debug("Nome não encontrado, usando fallback");
      return `${res.data.fldr_cname}`;
      
    } catch (err) {
      console.error("Erro ao buscar nome:", err);
      return `Pasta ${folderId}`;
    }
  };

  /**
   * 🔹 Carrega árvore a partir de uma pasta específica
   */
  loadFromSpecificFolder = async (folderId) => {
    debug("loadFromSpecificFolder:", folderId);
    
    try {
      this.setState({ loadingRoot: true });
      
      // 1. Carrega os filhos da pasta
      const res = await Axios.get(`${url.cBase}api/folder/tree`, {
        params: { nParentId: folderId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('cAccessToken')}`
        }
      });

      debug("Dados recebidos:", res.data);

      // 2. Extrai o parent ID
      const parentId = res.data.fldr_nid_parent && res.data.fldr_nid_parent !== "null" && res.data.fldr_nid_parent !== null 
        ? parseInt(res.data.fldr_nid_parent) 
        : null;
      
      debug("Parent ID:", parentId);

      // 3. Busca o nome real da pasta no parent
      let folderName = await this.fetchFolderName(folderId, parentId);
      
      debug("Nome da pasta:", folderName);

      // 4. Cria estrutura da árvore
      const treeData = {
        fldr_nid: folderId,
        fldr_cname: folderName,
        fldr_nid_parent: parentId,
        hasChildren: res.data.children && res.data.children.length > 0,
        children: res.data.children || []
      };

      // 5. Constrói navigationStack se houver parent
      const navigationStack = [];
      
      if (parentId !== null && parentId !== undefined) {
        debug("Adicionando parent na pilha:", parentId);
        navigationStack.push({
          folderId: parentId,
          folderName: folderName // Será carregado ao voltar
        });
      }

      debug("NavigationStack:", navigationStack);

      this.setState({
        treeData: { 
          fldr_nid: null, 
          fldr_cname: 'Root',
          children: [treeData]
        },
        currentFolderInfo: {
          fldr_nid: folderId,
          fldr_cname: folderName,
          fldr_nid_parent: parentId
        },
        navigationStack: navigationStack,
        selectedFolderId: folderId,
        loadingRoot: false,
        expandedNodes: {}
      });

    } catch (err) {
      console.error("Erro ao carregar pasta:", err);
      this.loadFolderTree();
    }
  };

  /**
   * 🔹 Carrega as pastas do nível especificado
   */
  loadFolderTree = async (nParentId = null) => {
    debug("loadFolderTree()", { parentId: nParentId });
  
    try {
      const loadingKey = nParentId || 'root';
      
      this.setState(prev => ({
        loadingNodes: { ...prev.loadingNodes, [loadingKey]: true }
      }));
  
      const res = await Axios.get(`${url.cBase}api/folder/tree`, {
        params: nParentId ? { nParentId } : {},
        headers: {
          Authorization: `Bearer ${localStorage.getItem('cAccessToken')}`
        }
      });
  
      debug("Resposta recebida:", res.data);
  
      const data = res.data;
  
      this.setState(prevState => {
        let newTree;
        let newExpandedNodes = { ...prevState.expandedNodes };

        if (nParentId === null) {
          debug("Carregando árvore raiz");
          newTree = data;
        } else {
          debug("Carregando filhos da pasta:", nParentId);
          newTree = this.insertChildrenInTree(prevState.treeData, nParentId, data.children);
          newExpandedNodes[nParentId] = true;
        }

        const newLoadingNodes = { ...prevState.loadingNodes };
        delete newLoadingNodes[loadingKey];

        return {
          treeData: newTree,
          expandedNodes: newExpandedNodes,
          loadingNodes: newLoadingNodes,
          loadingRoot: false
        };
      });
  
    } catch (err) {
      console.error("ERRO loadFolderTree:", err);
      this.setState(prev => {
        const newLoadingNodes = { ...prev.loadingNodes };
        delete newLoadingNodes[nParentId || 'root'];
        return {
          loadingNodes: newLoadingNodes,
          loadingRoot: false
        };
      });
    }
  };

  /**
   * 🔹 Insere os filhos carregados na posição correta da árvore
   */
  insertChildrenInTree = (tree, parentId, children) => {
    if (!tree) return tree;

    const cloneTree = (node) => {
      const cloned = { ...node };
      if (cloned.children && Array.isArray(cloned.children)) {
        cloned.children = cloned.children.map(child => cloneTree(child));
      }
      return cloned;
    };

    const newTree = cloneTree(tree);

    const updateNode = (node) => {
      if (node.fldr_nid === parentId) {
        debug("ENCONTROU! Inserindo filhos");
        node.children = children;
        return true;
      }
      
      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        for (let child of node.children) {
          if (updateNode(child)) return true;
        }
      }
      
      return false;
    };

    updateNode(newTree);
    
    if (newTree.children && Array.isArray(newTree.children)) {
      for (let child of newTree.children) {
        if (updateNode(child)) break;
      }
    }
    
    return newTree;
  };

  /**
   * 🔹 Navega para dentro de uma pasta
   */
  navigateIntoFolder = async (folder) => {
    debug("navigateIntoFolder:", folder);

    const { currentFolderInfo, navigationStack } = this.state;

    try {
      this.setState({ loadingRoot: true });

      // Carrega os filhos
      const res = await Axios.get(`${url.cBase}api/folder/tree`, {
        params: { nParentId: folder.fldr_nid },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('cAccessToken')}`
        }
      });

      debug("Filhos:", res.data);

      // Adiciona pasta atual na pilha
      const newStack = [...navigationStack, {
        folderId: currentFolderInfo?.fldr_nid || null,
        folderName: currentFolderInfo?.fldr_cname || 'Anterior',
        treeData: this.state.treeData
      }];

      // Nova árvore
      const newTreeData = {
        fldr_nid: null,
        fldr_cname: 'Root',
        children: [{
          fldr_nid: folder.fldr_nid,
          fldr_cname: folder.fldr_cname,
          fldr_nid_parent: res.data.fldr_nid_parent,
          hasChildren: res.data.children && res.data.children.length > 0,
          children: res.data.children || []
        }]
      };

      this.setState({
        treeData: newTreeData,
        currentFolderInfo: {
          fldr_nid: folder.fldr_nid,
          fldr_cname: folder.fldr_cname,
          fldr_nid_parent: res.data.fldr_nid_parent
        },
        navigationStack: newStack,
        selectedFolderId: folder.fldr_nid,
        expandedNodes: {},
        loadingRoot: false
      });

      debug("Navegação OK! Stack:", newStack.length);

    } catch (err) {
      console.error("Erro ao navegar:", err);
      this.setState({ loadingRoot: false });
    }
  };

  /**
   * 🔹 Volta para a pasta anterior
   */
  navigateBack = async () => {
    debug("navigateBack");

    const { navigationStack } = this.state;

    if (navigationStack.length === 0) {
      debug("Pilha vazia");
      await this.loadFolderTree();
      this.setState({
        navigationStack: [],
        currentFolderInfo: null
      });
      return;
    }

    const newStack = [...navigationStack];
    const previousState = newStack.pop();

    debug("Voltando para:", previousState);

    // Se tem treeData salvo, restaura
    if (previousState.treeData) {
      this.setState({
        treeData: previousState.treeData,
        currentFolderInfo: {
          fldr_nid: previousState.folderId,
          fldr_cname: previousState.folderName
        },
        selectedFolderId: previousState.folderId,
        navigationStack: newStack,
        expandedNodes: {}
      });
    } else if (previousState.folderId) {
      // Recarrega a pasta anterior
      this.setState({ navigationStack: newStack });
      await this.loadFromSpecificFolder(previousState.folderId);
    } else {
      // Volta para raiz
      await this.loadFolderTree();
      this.setState({
        navigationStack: [],
        currentFolderInfo: null
      });
    }
  };

  /**
   * 🔹 Alterna expansão de uma pasta
   */
  toggleFolder = async (folder, shouldSelect = false) => {
    const id = folder.fldr_nid;
    debug("toggleFolder()", { folderId: id });
  
    const { expandedNodes } = this.state;
    const isExpanded = expandedNodes[id];
  
    if (shouldSelect) {
      this.setState({ selectedFolderId: id });
      if (this.props.fnSelectFolder) {
        this.props.fnSelectFolder({ 
          target: { 
            name: 'fldr_nid', 
            value: id 
          } 
        });
      }
    }
  
    if (isExpanded) {
      this.setState(prev => ({
        expandedNodes: { ...prev.expandedNodes, [id]: false }
      }));
      debug("Fechando pasta");
      return;
    }

    debug("Abrindo pasta");
    
    if (folder.hasChildren && (!folder.children || folder.children.length === 0)) {
      debug("Carregando filhos");
      await this.loadFolderTree(folder.fldr_nid);
    } else {
      this.setState(prev => ({
        expandedNodes: { ...prev.expandedNodes, [id]: true }
      }));
    }
  };

  /**
   * 🔹 Renderiza um nó e seus filhos recursivamente
   */
  renderNode = (node, level = 0) => {
    const { expandedNodes, loadingNodes, selectedFolderId } = this.state;
    const { bLayerMode } = this.props;
    const isExpanded = expandedNodes[node.fldr_nid];
    const isLoading = loadingNodes[node.fldr_nid];
    const isSelected = selectedFolderId === node.fldr_nid;

    return (
      <li key={node.fldr_nid}>
        {node.hasChildren && (
          <a
            href="#toggle"
            onClick={(e) => { 
              e.preventDefault(); 
              e.stopPropagation();
              
              if (bLayerMode && level === 0) {
                this.navigateIntoFolder(node);
              } else {
                this.toggleFolder(node, false);
              }
            }}
            title={intl.get("TreeView.folder") || "Abrir/fechar pasta"}
            className={`open-folder ${isExpanded ? 'opened' : ''}`}
          >
            <i className="icon-next" />
          </a>
        )}

        <a
          href="#select"
          className={isSelected ? 'selected folder-name' : 'folder-name'}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleFolder(node, true);
          }}
          title={node.fldr_cname}
        >
          <i className="icon-folder" /> &nbsp;{node.fldr_cname}
        </a>

        {isLoading && (
          <img src={fLoading} alt="Carregando..." width="18" style={{ marginLeft: 8 }} />
        )}

        {isExpanded && node.children && node.children.length > 0 && (
          <ul style={{ display: 'block' }}>
            {node.children.map(child => this.renderNode(child, level + 1))}
          </ul>
        )}
      </li>
    );
  };

  render() {
    const { treeData, loadingRoot, navigationStack, currentFolderInfo } = this.state;
    const { className, cHeight } = this.props;
  
    debug("RENDER!", { 
      navigationStackSize: navigationStack.length,
      currentFolder: currentFolderInfo?.fldr_cname
    });

    if (loadingRoot) {
      return (
        <div className={`tree-view-layer ${className}`} style={{ maxHeight: cHeight }}>
          <span className="centered-title">
            <img src={fLoading} alt="Carregando..." />
          </span>
        </div>
      );
    }

    if (!treeData) {
      return (
        <div className={`tree-view-layer ${className}`} style={{ maxHeight: cHeight }}>
          <span className="centered-title">Nenhuma pasta encontrada</span>
        </div>
      );
    }

    // Se está em uma pasta específica (tem navigationStack), renderiza apenas os children
    const shouldRenderOnlyChildren = navigationStack.length > 0;
  
    return (
      <div 
        className={`tree-view-layer ${className}`} 
        style={{ 
          maxHeight: cHeight, 
          overflowY: 'auto',
          overflowX: 'auto'
        }}
      >
        <ul 
          className="root" 
          style={{ 
            display: 'block', 
            position: 'relative',
            whiteSpace: 'nowrap',
            minWidth: 'max-content'
          }}
        >
          {navigationStack.length > 0 && (
            <button
              type="button"
              className="goback"
              onClick={this.navigateBack}
              title="Voltar para pasta anterior"
              style={{
              zIndex: 1000
              }}
            >
              <img src={imgfSelectLeft} alt="Voltar" />
            </button>
          )}

          {shouldRenderOnlyChildren ? (
            // Renderiza apenas os children (quando está navegando em pastas específicas)
            treeData.children && treeData.children.length > 0 && 
            treeData.children.map(child => this.renderNode(child, 0))
          ) : (
            // Renderiza a raiz completa (quando está na view inicial)
            treeData.children && treeData.children.length > 0 &&
            treeData.children.map(rootFolder => (
              <li key={rootFolder.fldr_nid}>
                {rootFolder.hasChildren && (
                  <a
                    href="#toggle"
                    onClick={(e) => { 
                      e.preventDefault(); 
                      e.stopPropagation();
                      this.toggleFolder(rootFolder, false);
                    }}
                    title={intl.get("TreeView.folder") || "Abrir/fechar pasta"}
                    className={`open-folder ${this.state.expandedNodes[rootFolder.fldr_nid] ? 'opened' : ''}`}
                  >
                    <i className="icon-next" />
                  </a>
                )}

                <a
                  href="#select"
                  className={this.state.selectedFolderId === rootFolder.fldr_nid ? 'selected folder-name' : 'folder-name'}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleFolder(rootFolder, true);
                  }}
                  title={rootFolder.fldr_cname}
                >
                  <i className="icon-folder" /> &nbsp;{rootFolder.fldr_cname}
                </a>

                {this.state.loadingNodes[rootFolder.fldr_nid] && (
                  <img src={fLoading} alt="Carregando..." width="18" style={{ marginLeft: 8 }} />
                )}

                {this.state.expandedNodes[rootFolder.fldr_nid] && rootFolder.children && rootFolder.children.length > 0 && (
                  <ul style={{ display: 'block' }}>
                    {rootFolder.children.map(child => this.renderNode(child, 1))}
                  </ul>
                )}
              </li>
            ))
          )}
        </ul>
      </div>
    );
  }
}

TreeViewLayer.propTypes = {
  fnSelectFolder: PropTypes.func,
  cHeight: PropTypes.string,
  className: PropTypes.string,
  nFolderId: PropTypes.number,
  bLayerMode: PropTypes.bool,
};

TreeViewLayer.defaultProps = {
  fnSelectFolder: () => {},
  cHeight: '300px',
  className: '',
  nFolderId: null,
  bLayerMode: false,
};